import format from 'format';
import { FormikContextType, FormikValues, useFormikContext } from 'formik';
import React, { FC } from 'react';
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { ButtonCancel, ButtonDelete, ButtonSubmit, FlexRow } from './../index';
import './form-footer.css';

const footerPadding = {
  margin: '0.5rem 0.9rem',
};

// eslint-disable-off-next-line-off functional/no-mixed-type
type Props = {
  readonly isNew?: boolean;
  readonly permitDelete?: boolean;
  readonly loading?: boolean;
  readonly disabled?: boolean;
  readonly onDelete?: () => void;
  readonly noButtons?: boolean;
  readonly alwaysOn?: boolean;
  readonly injectFooterElements?: (formikCtx: FormikContextType<FormikValues>) => React.ReactNode;
  readonly onCancel?: () => void;
  children?: React.ReactNode;
};

function dirtyPaths(initial: any, current: any, basePath = ''): string[] {
  if (Object.is(initial, current)) return [];
  const initialIsObj = initial != null && typeof initial === 'object';
  const currentIsObj = current != null && typeof current === 'object';
  // primitive/type mismatch, null/undefined mismatch, array mismatch → όλο το node dirty
  if (!initialIsObj || !currentIsObj) return [basePath || '(root)'];
  if (Array.isArray(initial) !== Array.isArray(current)) return [basePath || '(root)'];
  const keys = new Set([...Object.keys(initial), ...Object.keys(current)]);
  const out: string[] = [];
  for (const k of keys) {
    const nextPath = basePath ? `${basePath}.${k}` : k;
    out.push(...dirtyPaths(initial[k], current[k], nextPath));
  }
  return out;
}

const FormFooter: FC<Props> = ({
  isNew,
  permitDelete,
  loading = false,
  onDelete = null,
  children = null,
  disabled = false,
  noButtons = false,
  alwaysOn = false,
  injectFooterElements,
  onCancel = null,
}) => {
  const formikCtx = useFormikContext();
  const { dirty, resetForm, isValid, errors, submitCount, values, initialValues } =
    useFormikContext();

  // if (dirty) {
  //   const paths = dirtyPaths(initialValues, values);
  //   (window as any).__dirtyPaths = paths;
  //   (window as any).__dirtyInitial = initialValues;
  //   (window as any).__dirtyValues = values;
  // }
  const { t, i18n } = useTranslation();

  const countErrors = (errs: unknown): number => {
    if (errs == null) return 0;
    if (typeof errs === 'string') return 1;
    if (Array.isArray(errs)) {
      return errs.reduce((sum, item) => sum + countErrors(item), 0);
    }
    if (typeof errs === 'object') {
      return Object.keys(errs).reduce(
        (sum, key) => sum + countErrors((errs as Record<string, unknown>)[key]),
        0,
      );
    }
    return 0;
  };

  const buildErrorMessage = (errors) => {
    const total = countErrors(errors);
    if (total <= 1)
      return i18n.language === 'gr'
        ? t('Προσοχή! Υπάρχει λάθος σε πεδίο της φόρμας.')
        : t("Attention! There is an error in the form's fields.");
    return format(
      i18n.language === 'gr'
        ? t('Προσοχή! Υπάρχουν %s λάθη στα πεδία της φόρμας.')
        : t("Attention! There are %s errors in the form's fields."),
      total,
    );
  };

  // const FormikErrors = ({ errors }) => {
  //   return (
  //     <div className="p-2">
  //       {Object.keys(errors).map((key, index) => {
  //         return (
  //           <div className="mb-1" key={index}>
  //             <strong>{key}</strong>: {errors[key]}
  //           </div>
  //         );
  //       })}
  //     </div>
  //   );
  // };
  const FormikErrors = ({ errors }) => {
    function formatErrorValue(value) {
      if (value == null) return '';
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) return value.map(formatErrorValue).join(', ');
      if (typeof value === 'object') {
        return Object.keys(value)
          .map((k) => `${k}: ${formatErrorValue(value[k])}`)
          .join('; ');
      }
      return String(value);
    }
    function formatErrorLine(index, key, value) {
      return (
        <div className="mb-1" key={index}>
          <strong>{key}</strong>: {formatErrorValue(value)}
        </div>
      );
    }
    return (
      <div className="p-2" style={{ backgroundColor: '#f8d7da', color: '#721c24' }}>
        {Object.keys(errors).map((key, index) => {
          if (Array.isArray(errors[key])) {
            return (
              <div key={index}>
                <strong>Πίνακας</strong>: {key}
                <br />
                {errors[key].map((lineError, subIndex) => {
                  const messages =
                    typeof lineError === 'object'
                      ? Object.keys(lineError)
                          .map((subKey) => `${subKey}: ${lineError[subKey]}`)
                          .join(', ')
                      : lineError;
                  return (
                    <div key={subIndex}>
                      <strong>Γραμμή {subIndex + 1}</strong>: {messages}
                    </div>
                  );
                })}
              </div>
            );
          }
          return formatErrorLine(index, key, errors[key]);
        })}
      </div>
    );
  };

  return (
    <Card.Footer style={{ padding: '0' }}>
      {/* -------- ERROR MESSAGES ---------- */}
      {!isValid && errors && submitCount > 0 && (
        <div
          style={{
            marginBottom: '-0.3rem',
            marginTop: '0.25rem',
            marginLeft: '0.9rem',
          }}
        >
          <OverlayTrigger
            delay={{ show: 1000, hide: 200 }}
            overlay={
              <Tooltip id="errors-tooltip" className="formik-errors-tooltip">
                <FormikErrors errors={errors} />
              </Tooltip>
            }
            placement="top"
          >
            <h5 className="text-danger text-center mb-2">{buildErrorMessage(errors)}</h5>
          </OverlayTrigger>
        </div>
      )}
      {/* -------- BUTTON ROW ---------- */}
      <FlexRow id="button-row" style={{ justifyContent: 'flex-start' }}>
        {/* -------- INJECT BUTTONS ---------- */}
        {injectFooterElements && (
          <div id="injectFooterElements" style={footerPadding}>
            {injectFooterElements(formikCtx)}
          </div>
        )}
        {/* -------- SAVE/CANCEL BUTTONS ---------- */}
        {!noButtons && (dirty || isNew || alwaysOn) && (
          <div id="save-cancel" style={footerPadding}>
            {/* {`alwaysOn: ${alwaysOn}, dirty: ${dirty}, loading: ${loading}, disabled: ${disabled}`} */}
            <ButtonSubmit
              disabled={!alwaysOn && (!dirty || loading || disabled)}
              loading={loading}
              style={footerPadding}
            />
            <span className="mx-1"></span>
            <ButtonCancel
              disabled={!dirty}
              onClick={() => {
                if (onCancel) {
                  onCancel();
                }
                resetForm();
              }}
            />
          </div>
        )}
        {/* -------- DELETE BUTTON ---------- */}
        {!dirty && !isNew && (children || permitDelete) && (
          <div id="delete" className={'d-flex'} style={{ ...footerPadding, marginLeft: 'auto' }}>
            {children}
            {permitDelete && (
              <div>
                <ButtonDelete className="ms-2" onClick={onDelete} />
              </div>
            )}
          </div>
        )}
      </FlexRow>
    </Card.Footer>
  );
};

export default FormFooter;
